// Mounted once at the App root so it floats on every page — matches the floating
// toggle in joylo-web (src/components/ThemeToggle.tsx) both visually and in
// mechanism: both read/write next-themes' storageKey "joylo-theme", and since the
// two apps share a domain, toggling in either one is picked up by the other.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Moon, Sun } from "@phosphor-icons/react";
import { useTheme } from "next-themes";
import { useLocation } from "react-router-dom";
import { useIsShellRoute } from "@/components/shell/AppShell";
import { useAuth } from "@/contexts/AuthContext";

export default function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  // Avoids a light/dark flash on first paint: next-themes can't know the persisted
  // theme until after mount, so render nothing until resolvedTheme is available.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // Shell pages carry their own toggle in the topbar; the editor's lives in the account menu and the button would cover the preview
  const inShell = useIsShellRoute();
  const { pathname } = useLocation();
  // Help renders inside the shell only for signed-in users; the public version keeps the floating toggle
  const { isAuthenticated } = useAuth();
  const inHelpShell = isAuthenticated && pathname === "/help";
  const inEditor = pathname.startsWith("/projects/") && pathname !== "/projects/all";
  const inAuth = ["/login", "/signup", "/forgot-password", "/reset-password", "/verify-email", "/check-email", "/accept-invite"].includes(pathname);
  if (!mounted || inShell || inHelpShell || inEditor || inAuth) return null;

  const isDark = resolvedTheme === "dark";
  return (
    <motion.button
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label="Toggle light/dark theme"
      whileHover={{ scale: 1.08, rotate: 12 }}
      whileTap={{ scale: 0.92 }}
      className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full bg-card border border-border flex items-center justify-center text-foreground"
    >
      {isDark ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
    </motion.button>
  );
}
